// Add API URL from environment variables
const API_URL = process.env.REACT_APP_API;

const getAiConfig = async (configKey) => {
  try {
    const response = await fetch(`${API_URL}/configs`, {
      method: 'GET', 
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${document.cookie.split(';').find(c => c.trim().startsWith('jwt=')).split('=')[1]}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const aiConfig = data.configuration.find(config => config.label === 'ai_config');
    
    if (!aiConfig || !aiConfig.value) {
      throw new Error('AI configuration not found');
    }

    const configArray = JSON.parse(aiConfig.value);
    const specificConfig = configArray.find(config => config.key === configKey);
    
    if (!specificConfig) {
      throw new Error(`Configuration not found for key: ${configKey}`);
    }

    return specificConfig;
  } catch (error) {
    console.error('Error fetching AI configuration:', error);
    throw error;
  }
};

const AiAnalysis = async (content_post, configKey, onStreamUpdate = null) => {
  try {
    const aiConfig = await getAiConfig(configKey);
    
    const payload = {
      max_tokens: aiConfig && aiConfig.max_tokens ? aiConfig.max_tokens : 10000,
      messages: [
        {
          role: 'system',
          content: aiConfig && aiConfig.prompt ? aiConfig.prompt : ''
        },
        {
          role: 'user',
          content: JSON.stringify(content_post)
        }
      ],
      model: aiConfig && aiConfig.model ? aiConfig.model : 'anthropic:3.5-sonnet',
      temperature: aiConfig && aiConfig.temperature ? aiConfig.temperature : 0.8,
      stream: onStreamUpdate ? true : false
    };

    const response = await fetch(aiConfig.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${aiConfig.token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle streaming response
    if (onStreamUpdate && response.body) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              
              if (data === '[DONE]') {
                break;
              }
              
              try {
                const parsed = JSON.parse(data);
                if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content) {
                  const content = parsed.choices[0].delta.content;
                  fullContent += content;
                  
                  // Clean content before sending to callback
                  let cleanContent = fullContent;
                  if (cleanContent.includes('<think>')) {
                    cleanContent = cleanContent.replace(/<think>.*?<\/think>/gs, '');
                  }
                  if (cleanContent.includes('```markdown')) {
                    cleanContent = cleanContent.replace('```markdown', '');
                  }
                  
                  onStreamUpdate(cleanContent);
                }
              } catch (parseError) {
                // Skip malformed JSON
                continue;
              }
            }
          }
        }
        
        return fullContent;
      } finally {
        reader.releaseLock();
      }
    } else {
      // Handle non-streaming response (fallback)
      const data = await response.json();
      let content = data.choices[0].message.content;
      
      // Remove <think>...</think> tags and their content if they exist
      if (content.includes('<think>')) {
          content = content.replace(/<think>.*?<\/think>/gs, '');
      }
      if (content.includes('```markdown')) {
          content = content.replace('```markdown', '');
      }
      return content;
    }
  } catch (error) {
    console.error('Error fetching AI recommendation:', error);
    throw error;
  }
};

export default AiAnalysis;